import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { addDoc, collection, doc, getDoc, serverTimestamp } from 'firebase/firestore';

export default function Channel() {
	const navigate = useNavigate();
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [created, setCreated] = useState(false);
	const [checkingPermission, setCheckingPermission] = useState(true);
	const [canCreateChannel, setCanCreateChannel] = useState(false);
	const [creating, setCreating] = useState(false);
	const [error, setError] = useState('');

	useEffect(() => {
		let alive = true;
		const loadPermission = async () => {
			const uid = localStorage.getItem('nexora_user_uid') || auth.currentUser?.uid;
			if (!uid) {
				navigate('/login');
				return;
			}
			try {
				const snap = await getDoc(doc(db, 'users', uid));
				if (alive) setCanCreateChannel(snap.exists() && snap.data()?.canCreateChannel === true);
			} catch (error) {
				if (alive) setCanCreateChannel(false);
			} finally {
				if (alive) setCheckingPermission(false);
			}
		};
		loadPermission();
		return () => { alive = false; };
	}, [navigate]);

	const createChannel = async event => {
		event.preventDefault();
		if (!name.trim()) return;
		const uid = localStorage.getItem('nexora_user_uid') || auth.currentUser?.uid;
		if (!canCreateChannel || !uid) return;

		try {
			setCreating(true);
			setError('');
			await addDoc(collection(db, 'channels'), {
				name: name.trim(),
				description: description.trim(),
				ownerUid: uid,
				members: [uid],
				createdAt: serverTimestamp(),
				updatedAt: serverTimestamp(),
				active: true,
			});
			setCreated(true);
			setName('');
			setDescription('');
		} catch (createError) {
			console.error('Channel creation failed:', createError);
			setError('Channel could not be created. Please try again.');
		} finally {
			setCreating(false);
		}
	};

	return (
		<main className="channel-page">
			<style>{`
				.channel-page { min-height: 100dvh; background: #f8fafc; padding: 20px; color: #0f172a; }
				.channel-shell { width: min(100%, 480px); min-height: calc(100dvh - 40px); margin: 0 auto; background: #fff; border: 1px solid #e2e8f0; border-radius: 24px; padding: 20px; box-shadow: 0 18px 45px rgba(15,23,42,.08); }
				.channel-header { display: flex; align-items: center; gap: 12px; margin-bottom: 24px; }
				.channel-back { width: 38px; height: 38px; border: 0; border-radius: 12px; background: #f1f5f9; color: #0f172a; font-size: 22px; cursor: pointer; }
				.channel-title { margin: 0; font-size: 22px; font-weight: 900; }
				.channel-subtitle { margin: 4px 0 0; color: #64748b; font-size: 12px; }
				.channel-form { display: grid; gap: 12px; }
				.channel-label { color: #475569; font-size: 12px; font-weight: 800; }
				.channel-input { width: 100%; border: 1px solid #e2e8f0; border-radius: 14px; padding: 13px; outline: none; font-size: 14px; resize: vertical; }
				.channel-input:focus { border-color: #62b875; box-shadow: 0 0 0 3px rgba(98,184,117,.14); }
				.channel-submit { margin-top: 6px; border: 0; border-radius: 14px; padding: 14px; background: #62b875; color: #fff; font-weight: 900; cursor: pointer; }
				.channel-success { margin-top: 16px; padding: 14px; border: 1px solid #bbf7d0; border-radius: 14px; background: #f0fdf4; color: #166534; font-size: 13px; font-weight: 700; }
				.channel-error { margin-top: 12px; padding: 12px; border: 1px solid #fecaca; border-radius: 12px; background: #fff7f7; color: #b91c1c; font-size: 12px; font-weight: 700; }
				.channel-denied { margin-top: 24px; display: grid; justify-items: center; gap: 8px; padding: 28px 20px; border: 1px solid #fecaca; border-radius: 18px; background: #fff7f7; color: #991b1b; text-align: center; }
				.channel-denied span { color: #7f1d1d; font-size: 13px; line-height: 1.5; }
				.channel-denied-icon { width: 42px; height: 42px; display: grid; place-items: center; border-radius: 50%; background: #fee2e2; color: #dc2626; font-size: 22px; font-weight: 900; }
			`}</style>
			<section className="channel-shell">
				<header className="channel-header">
					<button 
  type="button" 
  className="channel-back" 
  onClick={() => navigate(-1)} 
  aria-label="Go back"
  style={{ 
    background: 'none', 
    border: 'none', 
    cursor: 'pointer', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    padding: '8px'
  }}
>
  <svg 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2.5" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M15 18l-6-6 6-6"/>
  </svg>
</button>
					<div>
						<h1 className="channel-title">Create Channel</h1>
						<p className="channel-subtitle">Share updates with your Nexora audience.</p>
					</div>
				</header>
				{checkingPermission ? (
					<div className="channel-success">Checking channel creation permission...</div>
				) : !canCreateChannel ? (
					<div className="channel-denied">
						<div className="channel-denied-icon">!</div>
						<strong>Channel creation is unavailable</strong>
						<span>Only users approved by an Nexora can create a channel.</span>
					</div>
				) : (
				<form className="channel-form" onSubmit={createChannel}>
					<label className="channel-label" htmlFor="channel-name">Channel name</label>
					<input id="channel-name" className="channel-input" value={name} onChange={event => setName(event.target.value)} placeholder="Enter channel name" />
					<label className="channel-label" htmlFor="channel-description">Description</label>
					<textarea id="channel-description" className="channel-input" rows="4" value={description} onChange={event => setDescription(event.target.value)} placeholder="What is this channel about?" />
					<button type="submit" className="channel-submit" disabled={creating}>
						{creating ? 'Creating...' : 'Create New Channel'}
					</button>
				</form>
				)}
				{error && <div className="channel-error">{error}</div>}
				{created && <div className="channel-success">Channel details are ready. Your channel can now be connected to Firestore publishing.</div>}
			</section>
		</main>
	);
}
